import { Box, Divider, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BriaCanvas from "../../../../../../../components/common/Galleries/BriaCanvas.tsx";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";

import { Close } from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import BriaButton from "../../../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaSlider from "../../../../../../../components/common/BriaSlider/BriaSlider.tsx";
import BriaSwitch from "../../../../../../../components/common/BriaSwitch/BriaSwitch.tsx";
import { SegmentationMaskTypes } from "../../../../../../../constants/ImageToImageConstants.ts";
import { useSegmentationMaskProcessor } from "../../../../../../../hooks/useSegmentationMaskProcessor.tsx";
import { useSegmentationOverlay } from "../../../../../../../hooks/useSegmentationOverlay.tsx";
import type { PlaygroundImage } from "../../../../../../../models/playground.ts";
import useImageUtils from "../../../../../../../utils/useImageUtils.tsx";
import SegmentationOverlay from "../SegmentationOverlay/SegmentationOverlay.tsx";
import styles from "./ObjectsCanvas.module.scss";

interface ObjectsCanvasProps {
	selectedImageIndex: number;
	isLoading: boolean;
	setIsLoading: (status: boolean) => void;
	images: PlaygroundImage[];
}

const ObjectsCanvas = observer(({ selectedImageIndex, images, isLoading, setIsLoading }: ObjectsCanvasProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.imageToImage.config.features.eraseObject" });
	const { imageToImageStore } = useAppStore();
	const [imageUrl, setImageUrl] = useState(images[selectedImageIndex]?.url ?? "");
	const [panopticImageData, setPanopticImageData] = useState<ImageData | null>(null);
	const mainCanvasRef = useRef<HTMLCanvasElement>(null);
	const [isLoadingMasks, setIsLoadingMasks] = useState<boolean>(false);
	const {
		downloadAndProcessZip,
		getImagePixelsData,
		maskData,
		isLoading: isProcessingZip,
	} = useSegmentationMaskProcessor();
	const { processWhitePixels } = useImageUtils();

	const isManualMask = imageToImageStore.config.object_manipulation?.mask_type === SegmentationMaskTypes.MANUAL;

	useEffect(() => {
		setImageUrl(images[selectedImageIndex]?.url);
	}, [images, isLoading, selectedImageIndex]);

	const handleMaskClick = useCallback(
		(maskImage: HTMLImageElement) => {
			if (isManualMask) return;

			const overlayCanvas = imageToImageStore.brushCanvasRefs[0]?.canvasOverlayRef?.current;
			const mainCanvas = mainCanvasRef.current;

			if (overlayCanvas && mainCanvas && maskImage) {
				const ctx = overlayCanvas.getContext("2d");
				if (!ctx) return;

				ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
				overlayCanvas.width = mainCanvas.width;
				overlayCanvas.height = mainCanvas.height;

				const scaleX = mainCanvas.width / maskImage.width;
				const scaleY = mainCanvas.height / maskImage.height;

				const processedCanvas = processWhitePixels(maskImage, {
					r: 117,
					g: 51,
					b: 211,
					a: Math.floor(255 * 0.59),
				});

				ctx.save();
				ctx.scale(scaleX, scaleY);
				ctx.drawImage(processedCanvas, 0, 0);
				ctx.restore();

				imageToImageStore.toggleIsMaskApplied(true);
			}
		},
		[maskData.maskNumbers, imageToImageStore, mainCanvasRef],
	);

	const { overlayCanvasRef, isOverlayVisible } = useSegmentationOverlay({
		mainCanvasRef,
		panopticImageData,
		maskImages: maskData.maskImages,
		maskNumbers: maskData.maskNumbers,
		overlayOpacity: 0.59,
		onMaskClick: handleMaskClick,
	});

	const handleChangeMaskType = async () => {
		imageToImageStore.handleResetBrusher();
		imageToImageStore.isMaskApplied = false;
		imageToImageStore.handleConfigChange("object_manipulation", {
			...imageToImageStore.config.object_manipulation,
			mask_type: isManualMask ? SegmentationMaskTypes.AUTOMATIC : SegmentationMaskTypes.MANUAL,
		});

		if (imageToImageStore.config.object_manipulation?.mask_type === SegmentationMaskTypes.AUTOMATIC) {
			try {
				setIsLoadingMasks(true);
				const objectsMasks = await imageToImageStore.getMasks(imageUrl);
				if (objectsMasks) {
					downloadAndProcessZip(objectsMasks)
						.then((data) => {
							if (data.panopticImage) {
								const imageData = getImagePixelsData(data.panopticImage);
								setPanopticImageData(imageData);
							}
						})
						.catch((error) => {
							console.error("Failed to process zip file:", error);
						});
				}
			} catch (error) {
				console.error("Error getting masks:", error);
			} finally {
				setIsLoadingMasks(false);
			}
		}
	};

	return (
		<div className={styles.canvasWrapper}>
			<BriaCanvas
				url={imageUrl}
				enableBrush={isManualMask}
				brushConfigs={imageToImageStore.brushConfigs}
				isLoading={isLoading || imageToImageStore.isErasingImages}
				onCanvasSizeUpdate={() => {
					setIsLoading(false);
				}}
				canvasRef={(canvas: HTMLCanvasElement | null) => {
					// @ts-expect-error "needed for segmentation canvases"
					mainCanvasRef.current = canvas;
					if (!imageToImageStore.brushCanvasRefs[0]) {
						imageToImageStore.brushCanvasRefs[0] = {
							canvasRef: { current: null },
							canvasOverlayRef: { current: null },
						};
					}

					imageToImageStore.brushCanvasRefs[0].canvasRef.current = canvas;
				}}
				canvasOverlayRef={(overlayCanvas: HTMLCanvasElement | null) => {
					if (!imageToImageStore.brushCanvasRefs[0]) {
						imageToImageStore.brushCanvasRefs[0] = {
							canvasRef: { current: null },
							canvasOverlayRef: { current: null },
						};
					}
					imageToImageStore.brushCanvasRefs[0].canvasOverlayRef.current = overlayCanvas;
				}}
			/>

			{maskData.maskImages.length > 0 && !isManualMask && (
				<SegmentationOverlay
					overlayCanvasRef={overlayCanvasRef}
					isVisible={isOverlayVisible}
					mainCanvasRef={mainCanvasRef}
				/>
			)}

			<Box className={styles.objectsCanvasActionsContainer}>
				<Box className={styles.objectsCanvasActions}>
					<Box className={styles.maskTypeSwitcher}>
						<BriaSwitch
							checked={isManualMask}
							labelLeft={t("automatic")}
							labelRight={t("manual")}
							leftLoading={isProcessingZip || isLoadingMasks}
							onToggle={handleChangeMaskType}
						/>
					</Box>
					{isManualMask && (
						<>
							<Divider orientation="vertical" />
							<Box className={styles.brushSizeContainer}>
								<Typography className={styles.brushSizeLabel}>{t("brush")}</Typography>
								<BriaSlider
									value={imageToImageStore.brushConfigs.lineWidth}
									onChange={(e: any) => {
										imageToImageStore.brushConfigs.lineWidth = Number(e.target.value ?? 0);
									}}
									min={0}
									max={100}
									valueLabelDisplay={"on"}
									className={styles.brushSlider}
								/>
							</Box>
						</>
					)}

					<Divider orientation="vertical" />
					<BriaButton
						startIcon={<Close />}
						buttonType="textSmall"
						size="small"
						className={styles.discardMaskBtn}
						onClick={async () => {
							imageToImageStore.handleResetBrusher();
							imageToImageStore.toggleIsMaskApplied(false);
						}}
					>
						{t("discardMask")}
					</BriaButton>
				</Box>
			</Box>
		</div>
	);
});

export default ObjectsCanvas;
